import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { useProviderServices } from '../../../app/providers/AppServicesContext';
import { acquireApiAccessTokenAsync } from '../../../services/auth/AuthService';
import { upsertSession } from '../../../redux/features/chatSessions/chatSessionsSlice';
import { setSelectedSessionId } from '../../../redux/features/chatUi/chatUiSlice';
import { setDocuments } from '../../../redux/features/chatDocuments/chatDocumentsSlice';
import { ChatProviderType } from '../../../domain/chat/models/ChatProviderType';
import { migrateChatSessionHandler, MigrationProgress, MigrationStepType } from '../handlers/MigrateChatSessionHandler';
import { setMigrationContext } from '../../../redux/features/migrationContext/migrationContextSlice';

export interface UseMigrationState {
    step: MigrationStepType | null;
    chunkIndex?: number;
    totalChunks?: number;
    error: string | null;
}

export interface UseMigrationResult {
    state: UseMigrationState;
    migrate: (sessionId: string) => Promise<void>;
    cancel: () => void;
    reset: () => void;
}

const idle: UseMigrationState = { step: null, error: null };

export const useMigration = (): UseMigrationResult => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const providerServices = useProviderServices();
    const { instance } = useMsal();

    const sessionsById = useAppSelector((state) => state.chatSessions.byId);
    const agentsById = useAppSelector((state) => state.agents.byId);

    const [state, setState] = useState<UseMigrationState>(idle);
    const abortControllerRef = useRef<AbortController | null>(null);

    const reset = useCallback(() => setState(idle), []);

    const cancel = useCallback(() => {
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        setState(idle);
    }, []);

    const migrate = useCallback(
        async (sessionId: string): Promise<void> => {
            const session = sessionsById[sessionId];
            if (!session || session.providerType !== ChatProviderType.Assistants) return;

            const controller = new AbortController();
            abortControllerRef.current = controller;

            setState({ step: 'summarizing', error: null });

            try {
                const accessToken = await acquireApiAccessTokenAsync({
                    msalInstance: instance,
                });

                const onProgress = (progress: MigrationProgress): void => {
                    setState({
                        step: progress.step,
                        chunkIndex: progress.chunkIndex,
                        totalChunks: progress.totalChunks,
                        error: null,
                    });
                };

                const result = await migrateChatSessionHandler({
                    session,
                    agentsById,
                    accessToken,
                    providerServices,
                    onProgress,
                    signal: controller.signal,
                });

                dispatch(upsertSession(result.newSession));
                dispatch(setDocuments({ sessionId: result.newSession.id, documents: result.newDocuments }));
                dispatch(setMigrationContext({ sessionId: result.newSession.id, entry: { ...result.migrationContext, migratedFromId: sessionId } }));
                dispatch(setSelectedSessionId(result.newSession.id));
                navigate(`/chat/${result.newSession.id}`);

                setState({ step: 'complete', error: null });
            } catch (err) {
                if (err instanceof DOMException && err.name === 'AbortError') {
                    setState(idle);
                    return;
                }
                const message =
                    err instanceof Error ? err.message : 'Migration failed. Please try again.';
                setState({ step: null, error: message });
            } finally {
                abortControllerRef.current = null;
            }
        },
        [agentsById, dispatch, instance, navigate, providerServices, sessionsById],
    );

    return { state, migrate, cancel, reset };
};
